import ListHeader from "@/components/design-system/list-header";
import ListPagination from "@/components/design-system/list-pagination";
import {
  Table,
  TableSortConfig,
  type TableColumn,
} from "@/components/design-system/table";
import {
  deleteExistingStudent,
  fetchStudents,
  selectStudentActionLoading,
  selectStudentLoading,
  selectStudents,
  selectTotalItems,
} from "@/store/slices/studentSlice";
import { AppDispatch } from "@store";
import { Eye, SquarePen, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

// Using the StudentModel from types
import { Button } from "@/components/design-system/button";
import Modal from "@/components/design-system/Modal";
import SchoolSelect from "@/components/design-system/schoolSelect";
import StudentTypeSelect from "@/components/design-system/StudentTypeSelect";
import StudentTypeBadge, {
  resolveStudentType,
} from "@/modules/User-Management/components/StudentTypeBadge";
import { getSchoolOptionsService } from "@/service/dashboard.service";
import {
  fetchCsListDropdownOptions,
  type CsListGradeOption,
} from "@/service/base.service";
import { selectUserRole } from "@/store/slices/authSlice";
import { SchoolOption } from "@/types/school.model";
import { StudentModel, StudentQuery, type StudentListType } from "@/types/student.model";
import { toStudentListResourceParams } from "@/utils/studentListResourceParams";
import {
  capitalizeFirstLetter,
  CSVIcon,
  downloadFileFromBlob,
  evaluationColumns,
  ExcelIcon,
  FileFormats,
  hasMenuPermission,
  hasRolePermissions,
  PDFIcon,
  ROLE_PERMISSIONS,
  ROLES,
  successToast,
} from "@/utils/constants";
import ExportDropdown, {
  ExportOption,
} from "@/components/design-system/export-dropdown";
import { downloadStudentService } from "@/service/student.service";
import { setNavigationSource } from "@/store/slices/courseSlice";
import { getStudentCourseViewNavigation, resolveStudentClassName } from "@/utils/studentViewNavigation";

// Extended interface for UI display purposes
interface StudentDisplay extends StudentModel {
  sno?: number;
  school?: {
    name: string;
  };
}

const ITEMS_PER_PAGE = 10; // Or any other value you prefer

export default function StudentListSection() {
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);
  const [sortConfig, setSortConfig] = useState<TableSortConfig | undefined>(
    undefined
  );
  const [schoolOptions, setSchoolOptions] = useState<SchoolOption[]>([]);
  const [selectedSchool, setSelectedSchool] = useState<string>("");
  const [gradeOptions, setGradeOptions] = useState<CsListGradeOption[]>([]);
  const [selectedGrade, setSelectedGrade] = useState<string>("");
  const [selectedListType, setSelectedListType] = useState<StudentListType>("all");

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState<string | null>(null);

  const dispatch = useDispatch<AppDispatch>();
  const students = useSelector(selectStudents);
  const actionLoading = useSelector(selectStudentActionLoading);
  const loading = useSelector(selectStudentLoading);
  const totalItems = useSelector(selectTotalItems);
  const userRole = useSelector(selectUserRole);

  const navigate = useNavigate();

  // Map column keys to API sort keys
  const sortKeyMap: Record<string, string> = {
    name: "NME",
    reg_no: "REG",
    email: "EML",
    grade: "GRDE",
    course: "CSE",
    age: "AGE",
    "school.name": "SCH",
  };

  // Handle sorting
  const handleSort = (newSortConfig: TableSortConfig | null) => {
    // Convert null to undefined for compatibility with the Table component
    setSortConfig(newSortConfig || undefined);
    // Reset to first page when sort changes
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const columns: TableColumn<StudentDisplay>[] = [
    {
      key: "sno",
      title: "S.No",
      dataIndex: "sno",
      minWidth: 68,
      maxWidth: 68,
      align: "center",
      render: (_, __, index) => {
        // Calculate S.No based on current page and index
        const sno = (currentPage - 1) * ITEMS_PER_PAGE + index + 1;
        return (
          <div className="text-[#1F1F1F] text-[16px] font-medium text-center text-borderBlack">
            {String(sno).padStart(2, "0")}
          </div>
        );
      },
    },
    {
      key: "grade",
      title: "Grade",
      minWidth: 148,
      maxWidth: 148,
      sortable: true,
      sortKey: "grade",
      render: (_, record) => (
        <div className="text-[#1F1F1F] text-[16px] font-medium">
          {Array.isArray(record.grade)
            ? record.grade
              .map((grade) =>
                typeof grade === "object" && grade !== null
                  ? grade.grade
                  : grade
              )
              .join(", ")
            : typeof record.grade === "object" && record.grade !== null
              ? record.grade.grade
              : record.grade || "N/A"}
        </div>
      ),
    },

    {
      key: "school_name",
      title: "School Name",
      dataIndex: "school",
      minWidth: 250,
      maxWidth: 250,
      sortable: true,
      sortKey: "school.name",
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record.school?.name}
        </div>
      ),
    },
    {
      key: "reg_no",
      title: "Unique ID",
      dataIndex: "reg_no",
      minWidth: 145,
      maxWidth: 145,
      sortable: true,
      sortKey: "reg_no",
      render: (_, record) => (
        <div className="text-[#1F1F1F] text-[16px] font-medium">
          {record.reg_no}
        </div>
      ),
    },
    {
      key: "name",
      title: "Student Name",
      dataIndex: "name",
      minWidth: 250,
      maxWidth: 250,
      sortable: true,
      sortKey: "name",
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record.name}
        </div>
      ),
    },
    {
      key: "type",
      title: "Type",
      minWidth: 120,
      maxWidth: 120,
      align: "center",
      render: (_, record) => (
        <div className="flex justify-center">
          <StudentTypeBadge type={resolveStudentType(record)} />
        </div>
      ),
    },
    ...(selectedListType !== "course"
      ? [
          {
            key: "class_name",
            title: "Class Name",
            minWidth: 180,
            maxWidth: 220,
            render: (_: unknown, record: StudentDisplay) => (
              <div
                className="text-[#1F1F1F] text-[16px] font-medium"
                style={{
                  overflowWrap: "anywhere",
                  whiteSpace: "pre-wrap",
                }}
              >
                {resolveStudentClassName(record) || "—"}
              </div>
            ),
          } satisfies TableColumn<StudentDisplay>,
        ]
      : []),
    {
      key: "age",
      title: "Age",
      minWidth: 94,
      maxWidth: 94,
      sortable: true,
      sortKey: "age",
      render: (_, record) => (
        <div className="text-[#1F1F1F] text-[16px] font-medium">
          {record.age || "16"}
        </div>
      ),
    },
    {
      key: "gender",
      title: "Gender",
      minWidth: 148,
      maxWidth: 148,
      render: (_, record) => (
        <div className="text-[#1F1F1F] text-[16px] font-medium">
          {capitalizeFirstLetter(record.gender) || "N/A"}
        </div>
      ),
    },
    {
      key: "email",
      title: "Email Address",
      minWidth: 249,
      maxWidth: 249,
      sortable: true,
      sortKey: "email",
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
          style={{
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {record.email || "N/A"}
        </div>
      ),
    },
    {
      key: "course_completion",
      title: "Course Completion %",
      dataIndex: "course_completion",
      minWidth: 200,
      maxWidth: 200,
      render: (_, record) => (
        <div
          className="text-[#1F1F1F] text-[16px] font-medium"
        >
          {record.course_completion}
        </div>
      ),
    },
    ...evaluationColumns(selectedGrade)
  ];

  if (
    hasRolePermissions(userRole?.permissions as string[], [
      ROLE_PERMISSIONS.ACTION_VIEW_STUDENT,
      ROLE_PERMISSIONS.ACTION_EDIT_STUDENT,
      ROLE_PERMISSIONS.ACTION_DELETE_STUDENT,
    ])
  ) {
    columns.push({
      key: "action",
      title: "Action",
      minWidth: 202,
      maxWidth: 202,
      align: "left",
      fixed: "right",
      render: (_, record) => (
        <div className="flex items-center justify-start space-x-2">
          {hasMenuPermission(
            ROLE_PERMISSIONS.ACTION_VIEW_STUDENT,
            userRole?.permissions as string[]
          ) && (
              <button
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                title="View"
                onClick={() => {
                  const target = getStudentCourseViewNavigation(record);
                  if (!target) return;

                  dispatch(setNavigationSource("student-list"));
                  navigate(target.path, { state: target.state });
                }}
              >
                <Eye className="w-6 h-6 text-[#1C1B1F]" />
              </button>
            )}
          {hasMenuPermission(
            ROLE_PERMISSIONS.ACTION_EDIT_STUDENT,
            userRole?.permissions as string[]
          ) && (
              <button
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                title="Edit"
                onClick={() =>
                  navigate(`/user-management/student/edit-student/${record.id}`)
                }
              >
                <SquarePen className="w-6 h-6 text-[#1C1B1F]" />
              </button>
            )}
          {hasMenuPermission(
            ROLE_PERMISSIONS.ACTION_DELETE_STUDENT,
            userRole?.permissions as string[]
          ) && (
              <button
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                title="Delete"
                onClick={() => {
                  setDeleteModalOpen(true);
                  setStudentToDelete(record.id);
                }}
              >
                <Trash2 className="w-6 h-6 text-[#1C1B1F]" />
              </button>
            )}
        </div>
      ),
    });
  }

  // Handle delete student
  const handleDeleteStudent = () => {
    if (studentToDelete) {
      dispatch(deleteExistingStudent(studentToDelete))
        .unwrap()
        .then(() => {
          setDeleteModalOpen(false);
          setStudentToDelete(null);
        })
        .catch((error) => {
          console.log("Failed to delete Student:", error);
          // Modal stays open on error
        });
    }
  };

  // Handle export of student data as Excel, PDF or CSV
  const handleExport = async (type: "xl" | "pdf" | "csv") => {
    try {
      // Create query params from current filters
      const selectedOption = gradeOptions.find((o) => o.id === selectedGrade);
      const queryParams: StudentQuery = {
        page: currentPage,
        pageSize: ITEMS_PER_PAGE,
        search: searchValue || undefined,
        fromDate: fromDate,
        toDate: toDate,
        // teacherId: teacherId,
        schoolId: selectedSchool || undefined,
        type: selectedListType,
        ...toStudentListResourceParams(
          selectedListType,
          selectedGrade,
          selectedOption?.kind
        ),
      };
      const response = await downloadStudentService(type, queryParams);
      downloadFileFromBlob(response, type, "student_report");
      successToast(
        "Export Successful",
        `Students exported as ${FileFormats[type].name} successfully`
      );
    } catch (error) {
      console.error(`Error exporting students as ${type}:`, error);
    }
  };

  const exportOptions: ExportOption[] = [
    {
      label: "PDF",
      icon: <PDFIcon />,
      action: () => handleExport("pdf"),
    },
    {
      label: "Excel",
      icon: <ExcelIcon />,
      action: () => handleExport("xl"),
    },
    {
      label: "CSV",
      icon: <CSVIcon />,
      action: () => handleExport("csv"),
    },
  ];

  // Handle date range changes from the ListHeader component
  // Handle school selection change
  const handleSchoolChange = (schoolId: string) => {
    setSelectedSchool(schoolId);
    // Reset to first page when school filter changes
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  // Handle grade selection change
  const handleGradeChange = (gradeId: string) => {
    setSelectedGrade(gradeId);
    // Reset to first page when grade filter changes
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleListTypeChange = (type: StudentListType) => {
    setSelectedListType(type);
    setSelectedGrade("");
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  // Fetch school options for the dropdown when component mounts
  useEffect(() => {
    const fetchSchoolOptions = async () => {
      try {
        const response = await getSchoolOptionsService();
        if (response.data.data) {
          setSchoolOptions(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching school options:", error);
      }
    };

    fetchSchoolOptions();
  }, []);

  // Course / Class / All options from v2 dp/cs/list?type=…
  useEffect(() => {
    const fetchGradeOptions = async () => {
      try {
        const options = await fetchCsListDropdownOptions(selectedListType, {
          schoolId: selectedSchool || undefined,
        });
        setGradeOptions(options);
        setSelectedGrade((prev) => {
          if (prev && options.some((o) => o.id === prev)) return prev;
          return options[0]?.id ?? "";
        });
        hasFiltersReady.current = true;
      } catch (error) {
        console.error("Error fetching grade options:", error);
        setGradeOptions([]);
        setSelectedGrade("");
        hasFiltersReady.current = true;
      }
    };

    fetchGradeOptions();
  }, [selectedListType, selectedSchool]);

  const handleDateRangeChange = (
    startDate: Date | undefined,
    endDate: Date | undefined
  ) => {
    setFromDate(startDate);
    setToDate(endDate);
    // Reset to first page when date range changes
    if (currentPage !== 1) {
      setCurrentPage(1);
      // The page change will trigger a fetch
    }
  };

  // Track if component is mounted
  const isMounted = useRef(false);
  // Track if we need to fetch data
  const shouldFetch = useRef(true);
  // Track if filters (like grade options) are ready to fetch
  const hasFiltersReady = useRef(false);
  // Track previous filter state to detect changes excluding page number
  const prevFiltersKey = useRef<string | null>(null);

  // Function to fetch students data
  const fetchStudentsData = () => {
    // Prepare sort parameter if sortConfig exists
    let sortBy: string | undefined = undefined;
    if (sortConfig) {
      const apiSortKey = sortKeyMap[sortConfig.key];
      if (apiSortKey) {
        sortBy = `${apiSortKey}`;
      }
    }

    // Only fetch if we should
    if (shouldFetch.current) {
      shouldFetch.current = false; // Prevent additional fetches until explicitly allowed

      const selectedOption = gradeOptions.find((o) => o.id === selectedGrade);
      dispatch(
        fetchStudents({
          page: currentPage,
          pageSize: ITEMS_PER_PAGE,
          search: searchValue,
          fromDate: fromDate,
          toDate: toDate,
          sortBy: sortBy,
          schoolId: selectedSchool,
          type: selectedListType,
          ...toStudentListResourceParams(
            selectedListType,
            selectedGrade,
            selectedOption?.kind
          ),
        })
      );
    }
  };

  // Consolidated data-fetching effect
  useEffect(() => {
    if (!isMounted.current || !hasFiltersReady.current) return;

    // Build a key representing current filters excluding page
    const filtersKey = JSON.stringify({
      searchValue,
      selectedSchool,
      selectedGrade,
      selectedListType,
      fromDate: fromDate ? fromDate.toISOString() : null,
      toDate: toDate ? toDate.toISOString() : null,
      sortKey: sortConfig?.key || null,
      sortOrder: sortConfig?.direction || null,
    });

    // If filters changed (excluding page) and we're not on first page, reset to page 1 first
    if (prevFiltersKey.current !== filtersKey && currentPage !== 1) {
      prevFiltersKey.current = filtersKey;
      setCurrentPage(1);
      return;
    }

    // Allow fetching and perform fetch
    shouldFetch.current = true;
    fetchStudentsData();

    // Update previous filters key
    prevFiltersKey.current = filtersKey;
  }, [searchValue, selectedSchool, selectedGrade, selectedListType, fromDate, toDate, sortConfig, currentPage]);

  // Initial fetch on component mount
  useEffect(() => {
    isMounted.current = true;
    // Do not fetch immediately here; initial fetch will be triggered
    // after grades are loaded and default grade (if any) is selected.

    return () => {
      isMounted.current = false;
    };
  }, []);

  return (
    <div className="bg-white rounded-[12px] px-4 sm:px-6 md:px-[32px] w-full overflow-hidden h-full flex flex-1 flex-col">
      <ListHeader
        title="Students List"
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onDateRangeChange={handleDateRangeChange}
        leftAction={
          <>
            <StudentTypeSelect
              value={selectedListType}
              onChange={handleListTypeChange}
              className="min-w-[200px] w-full shrink-0 sm:w-[200px]"
            />
            <div className="min-w-[200px] w-full shrink-0 sm:w-[220px]">
              <SchoolSelect
                label=""
                placeholder="Select School"
                options={schoolOptions.map((option) => ({
                  value: option.id,
                  label: option.name,
                }))}
                value={selectedSchool}
                onChange={handleSchoolChange}
                showAbove={false}
                maxHeight="400px"
              />
            </div>
            <div className="min-w-[200px] w-full shrink-0 sm:w-[220px]">
              <SchoolSelect
                label=""
                placeholder={
                  selectedListType === "class"
                    ? "Select Class"
                    : selectedListType === "course"
                      ? "Select Course"
                      : "Select Course or Class"
                }
                options={gradeOptions.map((g) => ({ value: g.id, label: g.grade }))}
                value={selectedGrade}
                hideCloseIcon
                onChange={handleGradeChange}
                showAbove={false}
                maxHeight="400px"
              />
            </div>
          </>
        }
        rightAction={
          <>
            {/* Export Button */}
            {hasMenuPermission(
              ROLE_PERMISSIONS.BUTTON_EXPORT_STUDENT,
              userRole?.permissions as string[]
            ) && <ExportDropdown options={exportOptions} />}

            {hasMenuPermission(
              ROLE_PERMISSIONS.BUTTON_ADD_STUDENT,
              userRole?.permissions as string[]
            ) && userRole?.code !== ROLES.SUPER_ADMIN && (
                <Button
                  variant="primary"
                  size="md"
                  className="w-[161px] h-[48px] bg-[#5C88C5] text-white"
                  onClick={() => navigate("/user-management/student/add-student")}
                >
                  Add Student
                </Button>
              )}
          </>
        }
      />

      {/* Table Container with fixed header */}
      <div className="flex flex-1 flex-col border border-[#EDEDED] rounded-md overflow-x-auto">
        <div className="flex-1 overflow-auto h-full flex">
          <Table
            columns={columns}
            data={students}
            rowKey="id"
            showHeader={true}
            size="middle"
            sticky
            scroll={{ x: 1200 }}
            loading={loading}
            sortConfig={sortConfig}
            onSort={handleSort}
          />
        </div>
      </div>

      <ListPagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalItems={totalItems}
        itemsPerPage={ITEMS_PER_PAGE}
        itemsLabel="Students"
      />

      {/* Delete Student Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Student"
        width="728px"
        contentClassName="p-0"
      >
        <div className="p-6">
          <p className="text-[#7B7B7B] text-[24px] mb-8">
            Are you sure want to Delete Student ?
          </p>
        </div>
        <div className="flex justify-end border-t border-gray-200 p-4 gap-4">
          <Button
            variant="light"
            size="md"
            className="w-[120px] h-[48px] bg-[#fff] text-dark border-[1px] border-[#5C88C5]"
            onClick={() => setDeleteModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            className="w-[142px] h-[48px] bg-[#5C88C5] text-white"
            onClick={() => handleDeleteStudent()}
            disabled={actionLoading}
          >
            {actionLoading ? "Deleting..." : "Yes, Delete"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
